
import { supabase } from '@/integrations/supabase/client';
import { resetUserGameCounter } from './resetUserGameCounter';
import { serverTimeService } from '../time/serverTimeService';

/**
 * Service to handle game counter operations
 */
export const gameCounterService = {
  /**
   * Reset game counter for a specific user
   * @param userId The user ID
   * @returns A promise that resolves to a boolean indicating success
   */
  resetGameCounterForUser: async (userId: string): Promise<boolean> => {
    return resetUserGameCounter(userId);
  },
  
  /**
   * Reset game counter for the currently authenticated user
   * @returns A promise that resolves to a boolean indicating success
   */
  resetCurrentUserGameCounter: async (): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.error('No authenticated user found');
        return false;
      }
      
      console.log('Resetting game counter for current user:', user.id);
      return resetUserGameCounter(user.id);
    } catch (error) {
      console.error('Error resetting current user game counter:', error);
      return false;
    }
  },
  
  /**
   * Call admin function to reset all game counters (should be used by admins only)
   * @returns A promise that resolves to a boolean indicating success
   */
  resetAllGameCounters: async (): Promise<boolean> => {
    try {
      const { data, error } = await supabase.functions.invoke('reset-daily-game-counters');
      
      if (error) {
        console.error('Error calling reset function:', error);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error in resetAllGameCounters:', error);
      return false;
    }
  },

  /**
   * Set the games_allowed_per_day value in app_settings
   * @param value Number of games allowed per day
   * @returns A promise that resolves to a boolean indicating success
   */
  setGamesAllowedPerDay: async (value: number): Promise<boolean> => {
    try {
      if (value < 0) {
        console.error('Games allowed per day cannot be negative');
        return false;
      }

      const { error } = await supabase
        .from('app_settings')
        .update({ games_allowed_per_day: value })
        .eq('id', 1); // Assuming there is only one row in app_settings
      
      if (error) {
        console.error('Error updating games_allowed_per_day:', error);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error in setGamesAllowedPerDay:', error);
      return false;
    }
  },

  /**
   * Get the last check-in time for a user
   * @param userId The user ID
   * @returns A promise that resolves to the last check-in date or null
   */
  getLastCheckInTime: async (userId: string): Promise<string | null> => {
    try {
      const { data, error } = await supabase
        .from('customer_profiles')
        .select('last_check_in')
        .eq('id', userId)
        .single();
      
      if (error) {
        console.error('Error fetching last check-in time:', error);
        return null;
      }
      
      return data?.last_check_in || null;
    } catch (error) {
      console.error('Error in getLastCheckInTime:', error);
      return null;
    }
  }
};
